import React, { useEffect, useState } from 'react'
import { useTranslation } from "react-i18next"
import { Container, Row, Col, Card, OverlayTrigger, Tooltip, Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { CustomDataTable } from '../../../../components/design/Datatables/CustomDataTable'
import useAuth from '../../../../context/auth/useAuth'
import Loader from '../../../../components/design/Loader/Loader'
import { getPasses, getPassesByClub, removePass } from '../../../../actions/ticketing/passesActions'
import PassSummary from '../../../../components/ticketing/PassSummary'
import ClubBreadCrumbs from '../../../../components/breadcrumbs/ClubBreadCrumbs'
import TicketingSubMenu from '../../../../components/menus/TicketingSubMenu'
import Swal from 'sweetalert2'
import { sendEmailDownloadEntry } from '../../../../actions/downloads/downloadsActions'
import QRCode from 'qrcode';
import AddPass from '../../../../components/ticketing/AddPass'
import * as XLSX from 'xlsx';

export default function Passes({ usertype, isAdmin = false}) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { clientauth } = useAuth()

    const { passes, loading, result } = useSelector(state => state.pass)
    const { resultdownload } = useSelector(state => state.downloads)
    const { club } = useSelector(state => state.clubs)

    const [passSelected, setPassSelected] = useState(null)
    const [formType, setFormType] = useState(null)

    const [addPass, setAddPass] = useState(false)

    const [selectedPasses, setSelectedPasses] = useState([])
    const [selectAll, setSelectAll] = useState(false)
    const Checkbox = React.forwardRef(({ onClick, ...rest }, ref) => (
        <div className="form-check">
            <input
                type="checkbox"
                ref={ref}
                onClick={onClick}
                {...rest}
                className="form-check-input"
            />
        </div>
    ))
    
    Checkbox.displayName = 'Checkbox'

    const handleRowSelect = (state) => {
        setSelectedPasses(state.selectedRows.map(row => row._id))
        setSelectAll(state.selectedCount === passes.length)
    }

    const handleClearSelection = () => {
        setSelectedPasses([])
        setSelectAll(false)
    }

    const handleSelectAll = () => {
        setSelectedPasses(passes.map(pass => pass._id))
        setSelectAll(true)
    }

    const handleSendEmail = (e, passId) => {
        e.preventDefault()

        dispatch(sendEmailDownloadEntry(passId))
    }

    const handleSendMultipleEmails = async () => {
        if (selectedPasses.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'No hay abonos seleccionados',
                text: 'Por favor, selecciona al menos un abono para enviar correo'
            })
            return
        }
        
        const result = await Swal.fire({
            title: '¿Enviar correos?',
            text: `Se enviarán correos a ${selectedPasses.length} abonos seleccionados`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, enviar',
            cancelButtonText: 'Cancelar'
        })
        
        if (result.isConfirmed) {
            // Mostrar indicador de carga
            Swal.fire({
                title: 'Enviando correos...',
                text: `Procesando ${selectedPasses.length} abonos`,
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading()
                }
            })
            
            try {
                let successCount = 0
                let errorCount = 0
                
                // Enviar correos uno por uno para mejor control
                for (let i = 0; i < selectedPasses.length; i++) {
                    const passId = selectedPasses[i]
                    
                    // Actualizar progreso
                    Swal.update({
                        text: `Procesando ${i + 1} de ${selectedPasses.length}`
                    })
                    
                    try {
                        await dispatch(sendEmailDownloadEntry(passId))
                        successCount++
                    } catch (error) {
                        console.error(`Error enviando correo a abono ${passId}:`, error)
                        errorCount++
                    }
                }
                
                // Mostrar resultado final
                Swal.fire({
                    icon: successCount > 0 ? 'success' : 'error',
                    title: 'Proceso completado',
                    text: `Enviados: ${successCount}, Errores: ${errorCount}`,
                    confirmButtonText: 'Aceptar'
                })
                
                // Limpiar selección después del envío
                setSelectedPasses([])
                setSelectAll(false)
                
            } catch (error) {
                Swal.fire({
                    icon: 'error',
                    title: 'Error al enviar correos',
                    text: 'Ocurrió un error durante el envío de correos'
                })
            }
        }
    }

    const QRCodeImage = ({ identifier }) => {
        const [qrImageUrl, setQrImageUrl] = useState(null);

        useEffect(() => {
            const generateQRCode = async () => {
                try {
                    const qrImageUrl = await QRCode.toDataURL(identifier);
                    setQrImageUrl(qrImageUrl);
                } catch (error) {
                    console.error(error);
                }
            };
            generateQRCode();
        }, [identifier]);

        return qrImageUrl ? <img src={qrImageUrl} alt="qr code" style={{ width: '200px', height: '200px' }} /> : null;
    };

    const exportToExcel = () => {
        if (!passes || passes.length === 0) return;

        const dataToExport = passes.map(pass => {
            return {
                [t('passes.identifier')]: pass.identifier || '',
                [t('passes.season')]: pass.season?.name || '',
                [t('passes.sector')]: pass.sector?.name || '',
                [t('passes.status')]: pass.status === 0 ? t('passes.pending') :
                    pass.status === 1 ? t('passes.active') :
                        pass.status === 2 ? t('passes.canceled') :
                            t('passes.unknown'),
                [t('passes.price')]: pass.price || '',
                [t('passes.client')]: pass.client?.firstname || pass.personData?.firstname || '',
                [t('passes.email')]: pass.email || pass.personData?.email || '',
            }
        })

        const ws = XLSX.utils.json_to_sheet(dataToExport)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Passes')

        const fileName = `passes_${club?.name}_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(wb, fileName)
    }

    const columnsPasses = [
        {
            name: 'Seleccionar',
            selector: row => row._id,
            sortable: false,
            width: '60px',
            center: true,
            cell: row => (
                <input
                    type="checkbox"
                    checked={selectedPasses.includes(row._id)}
                    onChange={() => {
                        if (selectedPasses.includes(row._id)) {
                            setSelectedPasses(selectedPasses.filter(id => id !== row._id))
                        } else {
                            setSelectedPasses([...selectedPasses, row._id])
                        }
                    }}
                    className="form-check-input"
                />
            )
        },
        {
            name: t('passes.season'),
            cell: pass =>
                pass.season?.name,
            selector: (pass) => pass.season?.name,
            sortable: true,
        },
        {
            name: t('passes.passNumber'),
            width: '100px',
            cell: pass =>
                pass?.passNumber !== null ? pass.passNumber : '',
            selector: (pass) => pass?.passNumber,
            sortable: true,
        },
        // {
        //     name: 'QR CODIGOOOOOO',
        //     width: "320px",
        //     cell: pass =>
        //         <div style={{width: '100%', height: '90vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
        //             <QRCodeImage identifier={pass?.identifier} style={{width: '100%', height: '100%'}}/>
        //         </div>,
        //     selector: (pass) => pass.season.name,
        //     sortable: true,
        // },
        {
            name: t('passes.sector'),
            cell: pass =>
                pass.sector?.name,
            selector: (pass) => pass.sector?.name,
            sortable: true,
        }, {
            name: t('passes.status'),
            width: "120px",
            center: 'true',
            cell: pass =>
                pass.status === 0 ? t('passes.pending') :
                    pass.status === 1 ? t('passes.active') :
                        pass.status === 2 ? t('passes.canceled') :
                            t('passes.unknown'),
            selector: (pass) =>
                pass.status === 0 ? t('passes.pending') :
                    pass.status === 1 ? t('passes.active') :
                        pass.status === 2 ? t('passes.canceled') :
                            t('passes.unknown'),
            sortable: true,
        }, {
            name: t('passes.price'),
            center: 'true',
            cell: pass => (
                <div>
                    {pass.price} €
                </div>
            ),
            selector: (pass) => pass.price,
            sortable: true,
        }, {
            name: t('passes.client'),
            width: '30%',
            cell: pass => (
                <div>
                    {`${pass.client ? pass.client?.firstname : pass.personData?.firstname} ${pass.client ? pass.client?.lastname : pass.personData?.lastname ? pass.personData?.lastname : ''}`}
                    <br />
                    <small>Token: {pass.identifier}</small><br />
                    <small>{pass.passType?.name}</small><br />
                    <small>{pass.personData?.email || pass.email}</small>
                </div>
            ),
            selector: (pass) => `${pass.identifier} ${pass.client ? pass.client?.firstname : pass.personData?.firstname} ${pass.client ? pass.client?.lastname : pass.personData?.lastname} ${pass.passType?.name || ''}`,
            sortable: true,
        }, {
            name: t('passes.physical'),
            center: 'true',
            cell: pass => {
                const abonoFisico = pass.questions?.find(q => q.question === "Abono físico")
                return abonoFisico ? abonoFisico.answer : ""
            },
            selector: pass => {
                const abonoFisico = pass.questions?.find(q => q.question === "Abono físico")
                return abonoFisico ? abonoFisico.answer : ""
            },
            sortable: true,
        }, {
            name: t('global.actions'),
            center: 'true',
            width: '180px',
            cell: pass =>
                <div className='d-flex w-100 justify-content-center'>
                    <div
                        onClick={() => {
                            setPassSelected(pass)
                            setFormType("view")
                        }}
                        className='table-link me-2'>
                        <OverlayTrigger placement="top"
                            overlay={<Tooltip id="tooltip">{t('passes.seePass')}</Tooltip>}
                        >
                            <img className="cursor" src="/images/eyeopen.svg" alt="eyes" height={16} />
                        </OverlayTrigger>
                    </div>
                    <div
                        onClick={(e) => {
                            handleSendEmail(e, pass?._id)
                        }}
                        className='table-link me-2'>
                        <OverlayTrigger placement="top"
                            overlay={<Tooltip id="tooltip">{t('passes.sendemail')}</Tooltip>}
                        >
                            <i className='far fa-envelope'></i>
                        </OverlayTrigger>
                    </div>
                    <div
                        onClick={() => {
                            setPassSelected(pass)
                            setFormType("edit")
                        }}
                        className='table-link me-2'>
                        <OverlayTrigger placement="top"
                            overlay={<Tooltip id="tooltip">{t('passes.editPass')}</Tooltip>}
                        >
                            <img className="cursor" src="/images/pencil.svg" alt="pencil" height={16} />
                        </OverlayTrigger>
                    </div>
                    {isAdmin && (
                    <div
                        onClick={async () => {
                            const result = await Swal.fire({
                                title: t('global.confirmDelete'),
                                text: t('passes.confirmDeleteText'),
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: t('global.delete'),
                                cancelButtonText: t('global.cancel')
                            })
                            if (result.isConfirmed) {
                                await dispatch(removePass(pass._id))
                                if (isAdmin) {
                                    dispatch(getPasses())
                                } else if (club) {
                                    dispatch(getPassesByClub(club._id))
                                }
                            }
                        }}
                        className='table-link text-danger'>
                        <OverlayTrigger placement="top"
                            overlay={<Tooltip id="tooltip">{t('passes.deletePass')}</Tooltip>}
                        >
                            <i className="fas fa-times" style={{ cursor: 'pointer' }}></i>
                        </OverlayTrigger>
                    </div>
                    )}
                </div>,
            sortable: false,
        }
    ]

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'instant',
        })
        if (isAdmin) {
            dispatch(getPasses())
        } else {
            if (club) {
                dispatch(getPassesByClub(club._id))
            }
        }
        // eslint-disable-next-line
    }, [dispatch, t, clientauth])

    useEffect(() => {
        if (result) {
            switch (result) {
                case 'changeStatusSuccess':
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'success',
                        text: t('passes.changeStatusSuccess')
                    })
                    setPassSelected(null)
                    setFormType(null)
                    // dispatch(getPassesByClub(club._id))
                    dispatch({ type: "PASS_RESULT_RESET" })
                    break;
                default:
                    break;
            }
        }
    }, [result, club, dispatch, t])

    useEffect(() => {
        if (resultdownload) {
            let newresult = resultdownload
            dispatch({
                type: 'DOWNLOAD_RESET_RESULT'
            })
            switch (newresult) {
                case 'success':
                    // Solo mostrar mensaje individual si no estamos en modo múltiple
                    if (selectedPasses.length <= 1) {
                        Swal.fire({
                            showConfirmButton: true,
                            icon: 'success',
                            text: t('passes.emailsendsuccess')
                        })
                    }
                    break;
                case 'notentryexist':
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'warning',
                        text: t('passes.notentryexist')
                    })
                    break;
                case 'notemailexist':
                    Swal.fire({
                        showConfirmButton: true,
                        icon: 'warning',
                        text: t('passes.notemailexist')
                    })
                    break;
                default:
                    break;
            }
        }
    }, [resultdownload, dispatch, t, selectedPasses.length])

    const filteredPasses = passes?.filter(pass => {
        if (isAdmin) {
            return true // Mostrar todos los abonos si es admin
        } else {
            return pass.status !== 2 // Filtrar cancelados solo si es club
        }
    }) || []

    return (
        <Container className='cuerpo'>
            {
                isAdmin
                    ?
                    <Row>
                        <Col className='d-flex align-items-center'>
                            <h1>{t('menus.ticketing.passes')}</h1>
                        </Col>
                    </Row>
                    :
                    <>
                        <ClubBreadCrumbs usertype={usertype} club={club} />
                        <Row className="my-3">
                            <Col>
                                <TicketingSubMenu usertype={usertype} active="passes" />
                            </Col>
                        </Row>
                    </>
            }
            <Row className='my-3'>
                <Col>
                    <Card>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <h2>{t('passes.passesList')}</h2>
                                <div className="d-flex gap-2">
                                    {selectedPasses.length > 0 && (
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-muted">
                                                {selectedPasses.length} abonos seleccionados
                                            </span>
                                            <Button 
                                                variant="info" 
                                                onClick={handleSendMultipleEmails}
                                                disabled={selectedPasses.length === 0}
                                            >
                                                <i className="fas fa-envelope me-2"></i>
                                                Enviar correos ({selectedPasses.length})
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                size="sm"
                                                onClick={handleClearSelection}
                                            >
                                                <i className="fas fa-times me-1"></i>
                                                Limpiar
                                            </Button>
                                        </div>
                                    )}
                                    {passes && passes.length > 0 &&
                                        <Button variant="success" onClick={exportToExcel}>
                                            <i className="fas fa-file-excel me-2"></i>
                                            {t('global.exportToExcel')}
                                        </Button>
                                    }
                                    <Button variant="primary" type="button" className='ms-2' onClick={() => setAddPass(true)}>
                                        <i className='fas fa-plus' /> {t('passes.addPass')}
                                    </Button>
                                </div>
                            </div>
                            <hr className='mb-4' />
                            <div className='my-3'>
                                <Row>
                                    <Col>
                                        {
                                            loading
                                                ?
                                                <Loader />
                                                :
                                                <>
                                                    {
                                                        filteredPasses?.length > 0 ?
                                                            <>
                                                                <div className="d-flex align-items-center mb-3">
                                                                    <div className="form-check">
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id="selectAll"
                                                                            checked={selectAll}
                                                                            onChange={handleSelectAll}
                                                                        />
                                                                        <label className="form-check-label" htmlFor="selectAll">
                                                                            Seleccionar todos ({filteredPasses.length})
                                                                        </label>
                                                                    </div>
                                                                    {selectedPasses.length > 0 && (
                                                                        <span className="ms-3 text-muted">
                                                                            {selectedPasses.length} de {filteredPasses.length} seleccionados
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <CustomDataTable
                                                                    columns={columnsPasses}
                                                                    data={filteredPasses}
                                                                    exportable={false}
                                                                    printable={false}
                                                                    selectableRows
                                                                    selectableRowsComponent={Checkbox}
                                                                    selectableRowsComponentProps={{
                                                                        color: 'primary'
                                                                    }}
                                                                    onSelectedRowsChange={handleRowSelect}
                                                                    clearSelectedRows={selectedPasses.length === 0}
                                                                />
                                                            </>
                                                            :
                                                            <div className='contenedor-vacio text-center'>
                                                                <h4>{t('passes.noPasses')}</h4>
                                                                {t('passes.thereIsNoPassesPhrase')}
                                                            </div>
                                                    }
                                                </>
                                        }

                                    </Col>
                                </Row>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row >
            <PassSummary userType="client" passSelected={passSelected} closeModal={() => setTimeout(() => setPassSelected(null), 300)} formType={formType} usertype={usertype} />
            <AddPass userType="club" closeModal={() => { setAddPass(false) }} show={addPass} club={club} />
        </Container >
    )
}